/**
 * WMS Dashboard Main Component
 *
 * Configurable warehouse dashboard with drag-and-drop widgets.
 * Designed for large screen displays in warehouse environments.
 *
 * Features:
 * - Customizable widget layout
 * - Drag-and-drop editing
 * - Multiple layout presets
 * - Fullscreen mode for TV/monitor displays
 * - Auto-refreshing data
 * - Dark theme optimized for visibility
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { DashboardProvider, useDashboard } from './DashboardContext';
import DashboardToolbar from './components/DashboardToolbar';
import DashboardGrid from './components/DashboardGrid';
import WidgetSelector from './components/WidgetSelector';

/**
 * Dashboard Content Component (uses context)
 */
const DashboardContent: React.FC = () => {
  const { state } = useDashboard();
  const { isFullscreen, isEditMode } = state;
  const [showWidgetSelector, setShowWidgetSelector] = useState(false);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to exit edit mode or fullscreen
      if (e.key === 'Escape') {
        if (showWidgetSelector) {
          setShowWidgetSelector(false);
        }
      }
      // F11 or F for fullscreen
      if (e.key === 'F11' || (e.key === 'f' && !e.ctrlKey && !e.metaKey)) {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showWidgetSelector]);

  return (
    <div
      className={`
        h-full w-full flex flex-col
        bg-gray-900 text-white
        ${isFullscreen ? 'fixed inset-0 z-50' : ''}
      `}
    >
      {/* Toolbar */}
      <DashboardToolbar onAddWidget={() => setShowWidgetSelector(true)} />

      {/* Main grid area */}
      <div className="flex-1 min-h-0 relative">
        <DashboardGrid />

        {/* Edit mode indicator */}
        {isEditMode && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-blue-600 rounded-full shadow-lg">
            <p className="text-sm text-white font-medium">
              Redigeringsmodus - Dra widgets for å flytte
            </p>
          </div>
        )}
      </div>

      {/* Widget selector panel */}
      <WidgetSelector
        isOpen={showWidgetSelector}
        onClose={() => setShowWidgetSelector(false)}
      />
    </div>
  );
};

/**
 * WMS Dashboard Component
 *
 * Main export - wraps content with provider
 */
const WMSDashboard: React.FC = () => {
  return (
    <DashboardProvider>
      <DashboardContent />
    </DashboardProvider>
  );
};

export default WMSDashboard;
